import { FunctionComponent, useEffect, useState } from 'react'
import styles from './groupColors.module.scss'
import { useTranslation } from 'react-i18next'
import { DaycareGroup } from '@/types/DaycareGroup'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { UPDATE_DAYCARE_GROUP_COLORS } from '@/modules/API/daycareGroup/daycareGroupHandler'
import { useMutation } from '@apollo/client'
import { Construction } from '@mui/icons-material'
import { useAuth } from '@/modules/context/AuthContext'
import { Role } from '@/types/Role'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface GroupColorsProps {
    group?: DaycareGroup
    refetchGroups: () => void
    canWrite: boolean
}

type DaycareCSSColors = {
    [key: string]: string
}

const GroupColors: FunctionComponent<GroupColorsProps> = ({ group, refetchGroups, canWrite }: GroupColorsProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const baseCALDColors: DaycareCSSColors = {
        'daycare-dark': '#FBA530',
        'daycare-main': '#FBB24B',
        'daycare-light': '#FDD49A',
        'daycare-extra-light': '#FFFDF9',
        'parent-dark': '#4673B3',
        'parent-main': '#5992E3',
        'parent-light': '#C5D9F6',
        'parent-extra-light': '#FBFCFF'
    }

    const [colors, setColors] = useState<DaycareCSSColors>(baseCALDColors)

    function handleColorChange(key: string, value: string): void {
        setColors(
            (prev: DaycareCSSColors): DaycareCSSColors => ({
                ...prev,
                [key]: value
            })
        )
        document.documentElement.style.setProperty(`--${key}`, value)
    }

    function handleColorReset(): void {
        setColors(baseCALDColors)
        document.documentElement.style.removeProperty('--daycare-dark')
        document.documentElement.style.removeProperty('--daycare-main')
        document.documentElement.style.removeProperty('--daycare-light')
        document.documentElement.style.removeProperty('--daycare-extra-light')
        document.documentElement.style.removeProperty('--parent-dark')
        document.documentElement.style.removeProperty('--parent-main')
        document.documentElement.style.removeProperty('--parent-light')
        document.documentElement.style.removeProperty('--parent-extra-light')

        updateDaycareGroupColors({
            variables: {
                groupId: group?.id,
                colors: {
                    daycareDark: baseCALDColors['daycare-dark'],
                    daycareMain: baseCALDColors['daycare-main'],
                    daycareLight: baseCALDColors['daycare-light'],
                    daycareExtraLight: baseCALDColors['daycare-extra-light'],
                    parentDark: baseCALDColors['parent-dark'],
                    parentMain: baseCALDColors['parent-main'],
                    parentLight: baseCALDColors['parent-light'],
                    parentExtraLight: baseCALDColors['parent-extra-light']
                }
            }
        })
    }

    function tryParentColors(): void {
        document.documentElement.style.setProperty('--daycare-dark', colors['parent-dark'])
        document.documentElement.style.setProperty('--daycare-main', colors['parent-main'])
        document.documentElement.style.setProperty('--daycare-light', colors['parent-light'])
        document.documentElement.style.setProperty('--daycare-extra-light', colors['parent-extra-light'])
        showNotificationSuccess(t('colorsUpdated'))
    }

    function tryDaycareColors(): void {
        document.documentElement.style.setProperty('--daycare-dark', colors['daycare-dark'])
        document.documentElement.style.setProperty('--daycare-main', colors['daycare-main'])
        document.documentElement.style.setProperty('--daycare-light', colors['daycare-light'])
        document.documentElement.style.setProperty('--daycare-extra-light', colors['daycare-extra-light'])
        showNotificationSuccess(t('colorsUpdated'))
    }

    const [updateDaycareGroupColors] = useMutation(UPDATE_DAYCARE_GROUP_COLORS, {
        variables: {
            groupId: group?.id,
            colors: {
                daycareDark: colors['daycare-dark'],
                daycareMain: colors['daycare-main'],
                daycareLight: colors['daycare-light'],
                daycareExtraLight: colors['daycare-extra-light'],
                parentDark: colors['parent-dark'],
                parentMain: colors['parent-main'],
                parentLight: colors['parent-light'],
                parentExtraLight: colors['parent-extra-light']
            }
        },
        onCompleted: () => {
            showNotificationSuccess(t('colorsUpdated'))
            refetchGroups()
        },
        onError: () => {
            showNotificationError(t('errorUpdatingColors'))
        }
    })

    useEffect(() => {
        if (group?.colors) {
            setColors({
                'daycare-dark': group.colors.daycareDark,
                'daycare-main': group.colors.daycareMain,
                'daycare-light': group.colors.daycareLight,
                'daycare-extra-light': group.colors.daycareExtraLight,
                'parent-dark': group.colors.parentDark,
                'parent-main': group.colors.parentMain,
                'parent-light': group.colors.parentLight,
                'parent-extra-light': group.colors.parentExtraLight
            })
        }
    }, [group])

    const disabled: boolean =
        group?.colors?.daycareDark === colors['daycare-dark'] &&
        group?.colors?.daycareMain === colors['daycare-main'] &&
        group?.colors?.daycareLight === colors['daycare-light'] &&
        group?.colors?.daycareExtraLight === colors['daycare-extra-light'] &&
        group?.colors?.parentDark === colors['parent-dark'] &&
        group?.colors?.parentMain === colors['parent-main'] &&
        group?.colors?.parentLight === colors['parent-light'] &&
        group?.colors?.parentExtraLight === colors['parent-extra-light']

    return (
        <div className={styles.Container}>
            <div className={styles.Title}>
                <p>{t('colors')}</p>
            </div>
            <div className={styles.ColorContainer}>
                {Object.keys(colors).map((key) => (
                    <div key={key} className={styles.ColorItem}>
                        <label>{t(key)}</label>
                        <input
                            type='color'
                            value={colors[key as keyof typeof colors]}
                            disabled={!canWrite}
                            onChange={(e) => handleColorChange(key, e.target.value)}
                        />
                    </div>
                ))}
            </div>
            {canWrite && (
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('tryParentColors')}
                        customIcon={<Construction />}
                        className={styles.Button}
                        onClick={tryParentColors}
                    />
                    {user?.role === Role.cald && (
                        <ButtonTextIcon
                            type={'custom'}
                            customText={t('tryDaycareColors')}
                            customIcon={<Construction />}
                            className={styles.Button}
                            onClick={tryDaycareColors}
                        />
                    )}
                    <ButtonTextIcon
                        type={'update'}
                        onClick={updateDaycareGroupColors}
                        className={disabled ? styles.ButtonDisabled : styles.Button}
                        disabled={disabled}
                    />
                    <ButtonTextIcon type={'reset'} onClick={handleColorReset} className={styles.Button} />
                </div>
            )}
        </div>
    )
}

export default GroupColors
